import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Link } from 'react-router-dom';
import { businessInfo, operatorDisclosure } from '../utils/businessInfo';

export const RefundPolicyPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="Cancellation & Refund Policy - Swag Shots"
                description="Cancellation and refund policy for Swag Shots payments, purchases, failed transactions, and creator-related payment flows."
                url="https://www.swagshots.com/refund-policy"
            />

            <div className="pt-24 pb-16">
                <div className="container-custom max-w-4xl">
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Cancellation & Refund Policy</h1>
                        <div className="text-gray-600 mb-8">
                            <p>Effective Date: January 1, 2025</p>
                            <p className="mt-1">Operator: {businessInfo.operatorName}</p>
                            <p className="mt-1">GSTIN: {businessInfo.gstin}</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 mb-8 space-y-3">
                            <p className="text-gray-700 leading-relaxed">{operatorDisclosure}</p>
                            <p className="text-gray-700">
                                <span className="font-semibold text-gray-900">Registered Address:</span> {businessInfo.registeredAddress}
                            </p>
                            <p className="text-gray-700">
                                <span className="font-semibold text-gray-900">Support:</span>{' '}
                                <a href={`mailto:${businessInfo.email}`} className="text-primary-600 hover:text-primary-700 underline">
                                    {businessInfo.email}
                                </a>
                            </p>
                        </div>

                        <div className="space-y-8">
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    1. Scope
                                </h2>
                                <p className="text-gray-700 leading-relaxed">
                                    This policy applies to eligible payments made for Swag Shots through Android, web, approved payment gateway partners, or app store payment systems. Purchases are currently unavailable in the iOS app. If iOS digital goods or paid in-app features are offered in the future, they will be handled through Apple's in-app purchase system and Apple's refund process.
                                </p>
                            </section>

                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    2. Eligible Refunds
                                </h2>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                    <ul className="space-y-3 text-gray-700">
                                        <li>• Failed transactions where money was debited but the service, wallet credit, or benefit was not delivered</li>
                                        <li>• Duplicate charges for the same order or transaction</li>
                                        <li>• Wrongly charged payments confirmed after review by our support or payment partner team</li>
                                        <li>• Refunds required under applicable law, app store policy, or payment partner rules</li>
                                    </ul>
                                </div>
                            </section>

                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    3. Non-Refundable Items
                                </h2>
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                                    <ul className="space-y-3 text-gray-700">
                                        <li>• Used virtual coins, gifts, boosts, subscriptions, premium tools, or other consumed digital benefits</li>
                                        <li>• Creator rewards, payouts, withdrawals, or incentives that are rejected because of fraud, policy violations, incomplete KYC, tax issues, or suspicious activity</li>
                                        <li>• Purchases made through an app store where the app store requires users to request refunds directly from that app store</li>
                                    </ul>
                                </div>
                            </section>

                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    4. Cancellation
                                </h2>
                                <p className="text-gray-700 leading-relaxed">
                                    One-time digital purchases generally cannot be cancelled after successful delivery or use. Subscriptions, if available, can be cancelled through the relevant app store, account settings, or payment provider flow. Cancellation stops future renewal charges but does not automatically refund past charges unless required by the applicable platform policy.
                                </p>
                            </section>

                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    5. Refund Timeline
                                </h2>
                                <p className="text-gray-700 leading-relaxed">
                                    To request a refund, email {businessInfo.email} with your registered phone/email, transaction ID, payment date, amount, and a short explanation. Approved refunds are normally initiated within 7 business days after verification. The final credit timeline depends on the bank, app store, card network, UPI provider, or payment gateway.
                                </p>
                            </section>
                        </div>

                        <div className="bg-gradient-to-br from-primary-600 to-primary-500 text-white rounded-lg p-6 mt-8 text-center">
                            <h3 className="text-xl font-semibold mb-3">Payment Support</h3>
                            <p className="mb-4">
                                <a href={`mailto:${businessInfo.email}`} className="text-white underline hover:no-underline">
                                    {businessInfo.email}
                                </a>
                            </p>
                            <p className="text-primary-100 text-sm">
                                Operated by {businessInfo.operatorName}. GSTIN: {businessInfo.gstin}
                            </p>
                        </div>

                        <div className="text-center mt-8">
                            <Link
                                to="/"
                                className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                            >
                                Back to Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
